import { reconcileAfterServiceDetailsSave, ServiceDetailsConflict, type ServiceDetailsBase } from './billing/serviceDetailsConcurrency';
import { serviceDetailsPayload, type ServiceDetailsPart } from './billing/billingLineSource';
/**
 * ServiceDetailContent Component
 *
 * Inline content for editing booking-level service details (labor items, parts, extras).
 * Also displays and allows changing the assigned mechanic for this service.
 * Replaces the former ServiceDetailDialog — renders directly inside an ExpandableCard.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { LaborItemRow, persistedIdPattern } from './LaborItemRow';
import {
  applyAdoptedLaborItem,
  isHaynesProSnapshotsEnabled,
} from './haynespro/haynesproWorkOrderTime';
import {
  useHaynesProWorkOrderTime,
  type HaynesProAdoptResult,
} from '@/hooks/useHaynesProWorkOrderTime';
import { PartCatalogInput } from './PartCatalogInput';
import { PartBundlePicker } from './PartBundlePicker';
import { PartOrderStatusSelect } from './PartOrderStatusSelect';
import { VatRatePicker } from './VatRatePicker';
import { UnitSelect } from './UnitSelect';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import { UserAvatar } from '@/components/common';
import { useMechanicsWithSkills } from '@/hooks/useStaffUsers';
import { useServices } from '@/hooks/useServices';
import { splitByEligibility } from '@/utils/skill-matching';
import { MechanicCertificateLock } from './create/ResourceAssignmentFields';
import { CertificateOverrideDialog } from './CertificateOverrideDialog';
import {
  certificateRequiredSkills,
  isCertificateOverrideForbiddenError,
  mechanicCertificateIssue,
  parseCertificateGateError,
  type CertificateRequiredError,
  type SkillGateOverride,
} from './certificateGateError';
import {
  useUpdateBookingServiceDetails,
  type BookingServiceLaborItem,
  type BookingServicePart,
  type BookingServiceExtra,
  type PartOrderStatus,
} from '@/hooks/useWorkOrders';
import { useNotify } from '@/hooks/useNotify';
import type { PartBundleExpansion } from '@/hooks/useInventory';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { eurosToCents, lineTotalGrossCents, lineNetAfterDiscountCents } from '@/utils/money';
import { formatDuration, sumHours } from '@/utils/duration';
import { durationExceedsAllocatedSlot } from '@/utils/schedule-slot';
import type { VatRate } from '@/types';
import { distinctServiceTitle } from './serviceTitle';
import { isFixedPricePackage } from './fixedPricePackage';

export { applyAdoptedLaborItem } from './haynespro/haynesproWorkOrderTime';

export interface BookingServiceData {
  /** Catalog name: stays the heading (#3655). */
  name?: string;
  /** Work-specific title under the catalog name; null when none. */
  title?: string | null;
  /** Fixed-price fields: a fixed-price package locks its labor rows (#3655). */
  fixedPriceCents?: number | null;
  fixedPriceOverrideCents?: number | null;
  laborItems: BookingServiceLaborItem[];
  parts: BookingServicePart[];
  extras: BookingServiceExtra[];
  /** Technician remarks for this task (#1754) — customer-facing, shown on the invoice/print. */
  notes: string | null;
}

export interface ServiceDetailContentProps {
  bookingServiceId: string;
  detailsBase?: ServiceDetailsBase;
  /** Catalog service id — used to resolve this service's required skills. */
  serviceId: string;
  workOrderId: string;
  mechanicId: string | null;
  mechanicName: string | null;
  bookingServiceData: BookingServiceData | null;
  /** Parent service status — drives the inherited-pause display on steps (#1028). */
  serviceStatus?: string;
  /** Work order state forbids labor-item status commands (#1028). */
  statusActionsLocked?: boolean;
  onRemove: (bookingServiceId: string) => void;
  onMechanicChange: (
    bookingServiceId: string,
    mechanicId: string | null,
    options?: { skillGateOverride: SkillGateOverride },
  ) => void | Promise<void>;
  onSaved?: () => void;
  onDirtyChange?: (bookingServiceId: string, isDirty: boolean) => void;
  removing?: boolean;
  saving?: boolean;
  laborRateCents?: number | null;
  formatPrice?: (cents: number) => string;
  /** Hours already allocated to this work order (mechanic window). */
  allocatedSlotHours?: number | null;
  /** Labor hours from the other services on the same work order. */
  otherLaborHours?: number;
  vehicleId?: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  pricingType?: string | null;
  /** When false, labor-item hours stay visible but read-only (#2706). */
  hoursEditable?: boolean;
}

// Local labor-item draft: the server-shaped item plus a client-only key that
// survives id churn (temp id -> real id, or across autosave round-trips), so
// LaborItemRow never remounts (and loses focus) for a row the user hasn't
// actually removed (#1025).
export interface LaborItemDraft extends BookingServiceLaborItem {
  clientKey: string;
}

/**
 * Reconciles a fresh server labor-item list with the previous local drafts,
 * carrying over each row's `clientKey` so React keys stay stable across
 * refetches. Persisted rows match by id. Brand-new rows (temp id -> real id,
 * the client's first save of that row) have no id to match on, so they're
 * matched positionally against the previous not-yet-persisted rows — safe
 * because labor items are only ever appended, never reordered (#1025).
 */
export function mergeLaborItemDrafts(
  prevItems: LaborItemDraft[],
  serverItems: BookingServiceLaborItem[],
): LaborItemDraft[] {
  const prevByPersistedId = new Map(
    prevItems.filter((p) => persistedIdPattern.test(p.id)).map((p) => [p.id, p])
  );
  const leftoverPrev = prevItems.filter((p) => !persistedIdPattern.test(p.id));
  let leftoverIndex = 0;
  return serverItems.map((server) => {
    const matched = prevByPersistedId.get(server.id);
    if (matched) return { ...server, clientKey: matched.clientKey };
    const fallback = leftoverPrev[leftoverIndex++];
    return { ...server, clientKey: fallback?.clientKey ?? crypto.randomUUID() };
  });
}

/**
 * Builds the wire payload for the bulk details save. Recomputes `sortOrder`
 * from each array's current index rather than trusting the stale
 * `sortOrder` field — removing an item leaves the survivors' `sortOrder`
 * non-contiguous, and a later add would otherwise reuse an already-taken
 * value (which in turn would break `mergeLaborItemDrafts`'s positional
 * fallback matching for brand-new rows) (#1025). Keeps local ids for
 * reconciliation; the shared writer strips temporary labor and part ids and all
 * extra ids from the wire payload before the backend saves them (#3566).
 */
function toDetailsPayload(
  laborItems: LaborItemDraft[],
  parts: PartDraft[],
  extras: ExtraDraft[],
  notes: string | null,
  title: string,
  laborLocked: boolean,
) {
  return {
    title: title.trim() || null,
    notes,
    // A fixed-price package's labor rows are locked (#3655): never sent.
    ...(laborLocked ? {} : {
      laborItems: laborItems.map((item, index) => ({
        id: item.id,
        description: item.description,
        estimatedHours: item.estimatedHours,
        discountPercent: item.discountPercent,
        sortOrder: index,
      })),
    }),
    parts: parts.map((p, index) => ({ ...p, sortOrder: index })),
    extras: extras.map((e, index) => ({ ...e, sortOrder: index })),
  };
}

interface PartDraft {
  id?: string;
  partCategoryId: string | null;
  /** Inventory part the row is filled with; null for an unfilled category slot or a hand-typed row (#3842). */
  partId: string | null;
  name: string;
  isRequired: boolean;
  quantity: number;
  /** Exact gross (VAT-included) price the user typed, in whole cents (#1022). */
  unitPriceGrossCents: number;
  vatPercent: number;
  discountPercent: number;
  notes: string | null;
  sortOrder: number;
}

/** The payload leaves `partId` optional; a draft always carries it (#3842). */
function toPartDrafts(rows: ServiceDetailsPart[] | undefined): PartDraft[] {
  return (rows ?? []).map((row) => ({ ...row, partId: row.partId ?? null }));
}

interface ExtraDraft {
  id?: string;
  description: string;
  amountGrossCents: number;
  isTaxable: boolean;
  hasVariableQuantity: boolean;
  unit: string | null;
  quantity: number | null;
  sortOrder: number;
}

/** Default Finnish VAT rates used when tenant has none configured */
const defaultVatRates: VatRate[] = [
  { id: 'fi-25.5', label: '25,5 %', percentage: 25.5 },
  { id: 'fi-14', label: '14 %', percentage: 14 },
  { id: 'fi-10', label: '10 %', percentage: 10 },
  { id: 'fi-0', label: '0 %', percentage: 0 },
];

/** Plain Enter on a product-row text field adds a row; IME, Shift+Enter, and open list popups are left alone (#2674). */
function shouldAddProductRowOnEnter(event: KeyboardEvent): boolean {
  if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || event.repeat) {
    return false;
  }
  if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
    return false;
  }
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return false;
  if (target.getAttribute('aria-expanded') === 'true') return false;
  if (target.closest('[role="listbox"], [role="menu"], [role="combobox"][aria-expanded="true"]')) {
    return false;
  }
  return true;
}

export function ServiceDetailContent({
  bookingServiceId,
  detailsBase,
  serviceId,
  workOrderId,
  mechanicId,
  mechanicName,
  bookingServiceData,
  serviceStatus,
  statusActionsLocked = false,
  onRemove,
  onMechanicChange,
  onSaved,
  onDirtyChange,
  removing = false,
  saving = false,
  laborRateCents,
  formatPrice,
  allocatedSlotHours = null,
  otherLaborHours = 0,
  vehicleId,
  vehicleMake,
  vehicleModel,
  pricingType,
  hoursEditable = true,
}: ServiceDetailContentProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  // A fixed-price package's labor rows are locked; extra work is a separate service (#3655).
  const laborLocked = isFixedPricePackage({
    pricingType,
    fixedPriceCents: bookingServiceData?.fixedPriceCents,
    fixedPriceOverrideCents: bookingServiceData?.fixedPriceOverrideCents,
  });
  const laborEditable = hoursEditable && !laborLocked;
  const { data: mechanics = [] } = useMechanicsWithSkills();
  const { data: allServices = [] } = useServices({ isActive: true, sortBy: 'usage' });
  const mechanicMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.mechanic);
  const updateDetails = useUpdateBookingServiceDetails(bookingServiceId);
  const { capabilityQuery: haynesProCapabilityQuery } = useHaynesProWorkOrderTime(workOrderId);
  const haynesProEnabled = isHaynesProSnapshotsEnabled(haynesProCapabilityQuery.data);
  const notify = useNotify();
  const { tenantSettings } = useTenantSettings();

  // Resolve VAT rates from tenant settings or use defaults
  const vatRates: VatRate[] =
    (tenantSettings as Record<string, unknown> | undefined)?.vatRates as VatRate[] | undefined
    ?? defaultVatRates;
  const defaultVatRateId =
    (tenantSettings as Record<string, unknown> | undefined)?.defaultVatRateId as string | null | undefined
    ?? null;
  const defaultVatPercent = vatRates.find((r) => r.id === defaultVatRateId)?.percentage ?? vatRates[0]?.percentage ?? 25.5;

  const baseRef = useRef(detailsBase);
  const savingRef = useRef(false);
  const unmountedRef = useRef(false);
  const [localSaveError, setLocalSaveError] = useState(false);
  const [conflictBlocked, setConflictBlocked] = useState(false);
  const conflictBlockedRef = useRef(false);
  const [reloadGeneration, setReloadGeneration] = useState(0);
  const incomingBaseRef = useRef(detailsBase);
  incomingBaseRef.current = detailsBase;
  const incomingToken = detailsBase?.updatedAt;
  const [isDirty, setIsDirty] = useState(false);
  const haynesProBlocked = isDirty || updateDetails.isPending || saving;
  // Mirror of isDirty for use inside effects that must NOT re-run when it
  // changes (init hydration guard, autosave trigger, unmount flush).
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedMechanicId, setSelectedMechanicId] = useState<string>(mechanicId ?? '');
  const [certificateGate, setCertificateGate] = useState<CertificateRequiredError | null>(null);
  const [pendingMechanic, setPendingMechanic] = useState<{ value: string; previous: string } | null>(null);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  // Skill-aware mechanic selection (epic #1140 / #1133). Advisory, not blocking:
  // an unqualified mechanic can still be picked, but is grouped separately and
  // triggers a warning.
  const requiredSkills = useMemo(
    () => allServices.find((s) => s.id === serviceId)?.skills.filter((sk) => sk.isRequired) ?? [],
    [allServices, serviceId],
  );
  const requiredCertificateSkills = useMemo(
    () => certificateRequiredSkills(requiredSkills),
    [requiredSkills],
  );
  const hasSkillRequirements = requiredSkills.length > 0;
  const { eligible: eligibleMechanics, ineligible: ineligibleMechanics } = useMemo(
    () => splitByEligibility(mechanics, requiredSkills),
    [mechanics, requiredSkills],
  );
  const eligibleIds = useMemo(
    () => new Set(eligibleMechanics.map((m) => m.id)),
    [eligibleMechanics],
  );
  const selectedIsIneligible =
    hasSkillRequirements && selectedMechanicId !== '' && !eligibleIds.has(selectedMechanicId);

  const [laborItems, setLaborItems] = useState<LaborItemDraft[]>([]);
  const [parts, setParts] = useState<PartDraft[]>([]);
  const [extras, setExtras] = useState<ExtraDraft[]>([]);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState<string | null>(null);

  // Text inputs for gross price per part
  const [priceInputs, setPriceInputs] = useState<string[]>([]);
  // Text inputs for part quantities (supports decimals and comma separator)
  const [quantityInputs, setQuantityInputs] = useState<string[]>([]);
  // Text inputs for extra amounts in euros
  const [extraPriceInputs, setExtraPriceInputs] = useState<string[]>([]);
  // Text inputs for extra quantities (variable extras)
  const [extraQuantityInputs, setExtraQuantityInputs] = useState<string[]>([]);
  const partNameInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusNewPartNameRef = useRef(false);

  useLayoutEffect(() => {
    if (!focusNewPartNameRef.current) return;
    focusNewPartNameRef.current = false;
    partNameInputRefs.current[parts.length - 1]?.focus();
  }, [parts.length]);

  // Same for labor steps (#3276): Enter on a step row adds the next step and
  // lands in its description, so a mechanic can type a list of steps straight
  // through without reaching for the plus button.
  const laborDescriptionInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusNewLaborDescriptionRef = useRef(false);

  useLayoutEffect(() => {
    if (!focusNewLaborDescriptionRef.current) return;
    focusNewLaborDescriptionRef.current = false;
    laborDescriptionInputRefs.current[laborItems.length - 1]?.focus();
  }, [laborItems.length]);

  // Notify parent when dirty state changes — key by bookingServiceId so duplicate
  // catalog services (#418) get independent dirty tracking per row.
  useEffect(() => {
    onDirtyChange?.(bookingServiceId, isDirty);
  }, [isDirty, bookingServiceId, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(bookingServiceId, false);
    };
  }, [bookingServiceId, onDirtyChange]);

  useEffect(() => {
    // Don't re-hydrate from the server while the user has unsaved edits — an
    // autosave invalidates the work-order query, and the resulting refetch
    // would otherwise clobber changes the user is still making (#557).
    if (isDirtyRef.current || savingRef.current) return;
    const incoming = incomingBaseRef.current;
    if (incoming && baseRef.current && Date.parse(incoming.updatedAt) < Date.parse(baseRef.current.updatedAt)) return;
    baseRef.current = incoming;
    setSelectedMechanicId(mechanicId ?? '');
    if (bookingServiceData) {
      setTitle(distinctServiceTitle(bookingServiceData.name, bookingServiceData.title) ?? '');
      setNotes(bookingServiceData.notes);
      setLaborItems((prev) => mergeLaborItemDrafts(prev, bookingServiceData.laborItems));
      const mappedParts = bookingServiceData.parts.map((p) => ({
        id: p.id,
        partCategoryId: p.partCategoryId,
        partId: p.partId ?? null,
        name: p.name,
        isRequired: p.isRequired,
        quantity: p.quantity,
        unitPriceGrossCents: p.unitPriceGrossCents,
        vatPercent: p.vatPercent,
        discountPercent: p.discountPercent ?? 0,
        notes: p.notes,
        sortOrder: p.sortOrder,
      }));
      setParts(mappedParts);
      setPriceInputs(
        mappedParts.map((p) => (p.unitPriceGrossCents / 100).toFixed(2))
      );
      setQuantityInputs(
        mappedParts.map((p) => String(p.quantity))
      );
      const mappedExtras = bookingServiceData.extras.map((e) => ({
        id: e.id,
        description: e.description,
        amountGrossCents: e.amountGrossCents,
        isTaxable: e.isTaxable,
        hasVariableQuantity: e.hasVariableQuantity,
        unit: e.unit,
        quantity: e.quantity,
        sortOrder: e.sortOrder,
      }));
      setExtras(mappedExtras);
      setExtraPriceInputs(
        mappedExtras.map((e) => (e.amountGrossCents / 100).toFixed(2))
      );
      setExtraQuantityInputs(
        mappedExtras.map((e) => e.quantity != null ? String(e.quantity) : '')
      );
    } else {
      setTitle('');
      setNotes(null);
      setLaborItems([]);
      setParts([]);
      setPriceInputs([]);
      setQuantityInputs([]);
      setExtras([]);
      setExtraPriceInputs([]);
      setExtraQuantityInputs([]);
    }
    setIsDirty(false);
  }, [mechanicId, bookingServiceData, incomingToken, reloadGeneration]);

  const commitMechanic = async (
    value: string,
    options?: { skillGateOverride: SkillGateOverride },
  ) => {
    if (options) {
      await onMechanicChange(bookingServiceId, value || null, options);
    } else {
      await onMechanicChange(bookingServiceId, value || null);
    }
  };

  const handleMechanicChange = (value: string) => {
    const previous = selectedMechanicId;
    setSelectedMechanicId(value);
    if (value !== '') mechanicMru.record(value);
    void (async () => {
      try {
        await commitMechanic(value);
      } catch (error) {
        const parsed = parseCertificateGateError(error);
        if (parsed) {
          setPendingMechanic({ value, previous });
          setCertificateGate(parsed);
          return;
        }
        if (isCertificateOverrideForbiddenError(error)) {
          notify.error(tc('error'), t('certificateGate.forbidden'));
        }
        setSelectedMechanicId(previous);
      }
    })();
  };

  const handleCertificateOverrideClose = () => {
    if (overrideSubmitting) return;
    if (pendingMechanic) setSelectedMechanicId(pendingMechanic.previous);
    setCertificateGate(null);
    setPendingMechanic(null);
  };

  const handleCertificateOverrideSubmit = async (reason: string) => {
    if (!pendingMechanic) return;
    setOverrideSubmitting(true);
    try {
      await commitMechanic(pendingMechanic.value, { skillGateOverride: { reason } });
      setCertificateGate(null);
      setPendingMechanic(null);
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const latestRef = useRef({ laborItems, parts, extras, notes, title, priceInputs, quantityInputs, extraPriceInputs, extraQuantityInputs });
  latestRef.current = { laborItems, parts, extras, notes, title, priceInputs, quantityInputs, extraPriceInputs, extraQuantityInputs };
  const hasUncommittedInputs = useCallback((draft: typeof latestRef.current) =>
    draft.parts.some((p, i) => draft.priceInputs[i] !== (p.unitPriceGrossCents / 100).toFixed(2) || draft.quantityInputs[i] !== String(p.quantity)) ||
    draft.extras.some((e, i) => draft.extraPriceInputs[i] !== (e.amountGrossCents / 100).toFixed(2) || draft.extraQuantityInputs[i] !== (e.quantity == null ? '' : String(e.quantity))), []);
  const autosave = useCallback(async () => {
    if (savingRef.current || conflictBlockedRef.current || !baseRef.current || hasUncommittedInputs(latestRef.current)) return;
    savingRef.current = true;
    setLocalSaveError(false);
    const base = baseRef.current;
    const sent = toDetailsPayload(laborItems, parts, extras, notes, title, laborLocked);
    try {
      const saved = await updateDetails.mutateAsync({ workOrderId, bookingServiceId, data: sent, base });
      const current = latestRef.current;
      const desired = toDetailsPayload(current.laborItems, current.parts, current.extras, current.notes, current.title, laborLocked);
      const editedDuringSave = JSON.stringify(desired) !== JSON.stringify(sent);
      // The committed response supplies exact identities; newer local edits remain dirty.
      baseRef.current = saved;
      const merged = editedDuringSave ? reconcileAfterServiceDetailsSave(sent, desired, saved) : serviceDetailsPayload(saved.service);
      // An unmount during the first PUT still owes the newer committed edits a save.
      // Use this transaction's token/identities and keep the same conditional recovery.
      if (unmountedRef.current) {
        if (editedDuringSave && !hasUncommittedInputs(current))
          await updateDetails.mutateAsync({ workOrderId, bookingServiceId, data: merged, base: saved });
        onSaved?.();
        return;
      }
      setTitle(merged.title ?? '');
      setNotes(merged.notes ?? null);
      setLaborItems(prev => mergeLaborItemDrafts(prev, (merged.laborItems ?? []).map(row => ({ ...current.laborItems.find(x => x.id === row.id || saved.idMap?.[x.id] === row.id), ...saved.service.laborItems.find(x => x.id === row.id), ...row })) as BookingServiceLaborItem[]));
      setParts(toPartDrafts(merged.parts));
      setExtras(merged.extras ?? []);
      const retainInputs = <T extends { id?: string }>(rows: T[], oldRows: { id?: string }[], localRows: { id?: string }[], localText: string[], oldText: string[], format: (row: T) => string) => rows.map(row => {
        const same = (old: { id?: string }) => old.id === row.id || (old.id && saved.idMap?.[old.id] === row.id);
        const localIndex = localRows.findIndex(same);
        const oldIndex = oldRows.findIndex(same);
        return localIndex >= 0 && (oldIndex < 0 || localText[localIndex] !== oldText[oldIndex])
          ? localText[localIndex] ?? format(row) : format(row);
      });
      setPriceInputs(retainInputs(merged.parts ?? [], sent.parts, current.parts, current.priceInputs, priceInputs, p => (p.unitPriceGrossCents / 100).toFixed(2)));
      setQuantityInputs(retainInputs(merged.parts ?? [], sent.parts, current.parts, current.quantityInputs, quantityInputs, p => String(p.quantity)));
      setExtraPriceInputs(retainInputs(merged.extras ?? [], sent.extras, current.extras, current.extraPriceInputs, extraPriceInputs, e => (e.amountGrossCents / 100).toFixed(2)));
      setExtraQuantityInputs(retainInputs(merged.extras ?? [], sent.extras, current.extras, current.extraQuantityInputs, extraQuantityInputs, e => e.quantity == null ? '' : String(e.quantity)));
      setIsDirty(editedDuringSave || hasUncommittedInputs(current));
      onSaved?.();
    } catch (error) {
      if (error instanceof ServiceDetailsConflict) { conflictBlockedRef.current = true; if (!unmountedRef.current) setConflictBlocked(true); }
      if (!unmountedRef.current) setLocalSaveError(true);
      notify.error(tc('error'), t('detail.detailsSaveFailed'));
    } finally { savingRef.current = false; }
  }, [updateDetails, workOrderId, bookingServiceId, laborItems, parts, extras, notes, title, laborLocked, priceInputs, quantityInputs, extraPriceInputs, extraQuantityInputs, hasUncommittedInputs, onSaved, notify, t, tc]);

  // Autosave (#557): persist committed changes a short moment after the last
  // edit. Free-text money/quantity fields only reach committed state (parts /
  // extras) on blur, so this debounced snapshot never holds a half-typed value.
  // Notes IS free text but is short/rare enough to autosave on every keystroke
  // (debounced) rather than adding a blur-commit step just for this field.
  const committedSnapshot = useMemo(
    () => ({ laborItems, parts, extras, notes, title }),
    [laborItems, parts, extras, notes, title],
  );
  const debouncedSnapshot = useDebounce(committedSnapshot, 800);
  const autosaveRef = useRef(autosave);
  autosaveRef.current = autosave;
  useEffect(() => {
    if (isDirtyRef.current) void autosaveRef.current();
  }, [debouncedSnapshot]);

  // Flush a pending edit if the user leaves within the debounce window so the
  // last change isn't lost. Fire-and-forget on unmount (no setState).
  const flushRef = useRef<() => void>(() => {});
  flushRef.current = () => {
    const draft = latestRef.current;
    if (baseRef.current && !savingRef.current && !conflictBlockedRef.current && !hasUncommittedInputs(draft)) updateDetails.mutate({ workOrderId, bookingServiceId, data: toDetailsPayload(draft.laborItems, draft.parts, draft.extras, draft.notes, draft.title, laborLocked), base: baseRef.current });
  };
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (isDirtyRef.current) flushRef.current();
    };
  }, []);

  // Labor items handlers
  const addLaborItem = () => {
    setLaborItems((prev) => [
      ...prev,
      {
        id: `temp-${crypto.randomUUID()}`,
        clientKey: crypto.randomUUID(),
        description: '',
        estimatedHours: 0.5,
        discountPercent: 0,
        sortOrder: prev.length,
        status: 'pending' as const,
        startedAt: null,
        completedAt: null,
        completedBy: null,
        timeSource: 'manual',
        estimatedHoursOriginal: 0.5,
        editedBy: null,
        editedAt: null,
        editedByName: null,
      },
    ]);
    setIsDirty(true);
  };

  const updateLaborItem = <K extends keyof BookingServiceLaborItem>(
    index: number,
    field: K,
    value: BookingServiceLaborItem[K],
  ) => {
    setLaborItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    setIsDirty(true);
  };

  const restoreLaborItemHours = (index: number) => {
    setLaborItems((prev) =>
      prev.map((item, i) => {
        if (i !== index || item.estimatedHoursOriginal == null) return item;
        return {
          ...item,
          estimatedHours: item.estimatedHoursOriginal,
          editedBy: null,
          editedAt: null,
          editedByName: null,
        };
      }),
    );
    setIsDirty(true);
  };

  const removeLaborItem = (index: number) => {
    setLaborItems((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const applyHaynesProAdoption = (result: HaynesProAdoptResult) => {
    // Patch only the adopted row. Other unsaved labor drafts stay as-is even
    // if the work-order query invalidates — the dirty guard still skips
    // full rehydration (#1442).
    setLaborItems((prev) => applyAdoptedLaborItem(prev, result.laborItem));
  };

  const bookingDurationHours = sumHours(laborItems.map((item) => item.estimatedHours)) + otherLaborHours;
  const hasScheduleConflict = durationExceedsAllocatedSlot(bookingDurationHours, allocatedSlotHours);

  // Parts handlers
  const addPart = () => {
    setParts((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        partCategoryId: null,
        partId: null,
        name: '',
        isRequired: true,
        quantity: 1,
        unitPriceGrossCents: 0,
        vatPercent: defaultVatPercent,
        discountPercent: 0,
        notes: null,
        sortOrder: prev.length,
      },
    ]);
    setPriceInputs((prev) => [...prev, '0.00']);
    setQuantityInputs((prev) => [...prev, '1']);
    setIsDirty(true);
  };

  // Part bundle (#3350): append every expanded line as an ordinary part row. Functional
  // updates keep rows and typed values added while the bundle was loading, and the three
  // parallel arrays grow together exactly as in addPart. Ids are temporary (`temp-…`), so the
  // shared writer strips them and the server assigns the real ones; the normal autosave
  // (version token, conflict handling, merge-after-save) is the only write path.
  const addBundleParts = (expansion: PartBundleExpansion) => {
    if (expansion.lines.length === 0) return;
    const added = expansion.lines.map((line) => ({
      id: `temp-${crypto.randomUUID()}`,
      partCategoryId: line.categoryId,
      partId: line.partId,
      name: line.name,
      isRequired: true,
      quantity: line.quantity,
      unitPriceGrossCents: line.unitPriceGrossCents,
      // The gross price was derived from the catalog net at this rate; keeping it makes the
      // gross -> net write path return that net.
      vatPercent: expansion.vatPercent,
      discountPercent: 0,
      notes: null,
    }));
    setParts((prev) => [...prev, ...added.map((row, offset) => ({ ...row, sortOrder: prev.length + offset }))]);
    setPriceInputs((prev) => [...prev, ...added.map((row) => (row.unitPriceGrossCents / 100).toFixed(2))]);
    setQuantityInputs((prev) => [...prev, ...added.map((row) => String(row.quantity))]);
    setIsDirty(true);
  };

  const updatePart = (index: number, field: keyof PartDraft, value: string | number | boolean | null) => {
    setParts((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    setIsDirty(true);
  };

  const handleQuantityInputChange = (index: number, value: string) => {
    setQuantityInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handleQuantityBlur = (index: number) => {
    const raw = quantityInputs[index] ?? '0';
    const normalized = raw.replace(',', '.');
    const qty = parseFloat(normalized);
    const validQty = isNaN(qty) || qty < 0 ? 0 : qty;
    updatePart(index, 'quantity', validQty);
    setQuantityInputs((prev) =>
      prev.map((v, i) => (i === index ? String(validQty) : v))
    );
  };

  const handlePriceInputChange = (index: number, value: string) => {
    setPriceInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handlePriceBlur = (index: number) => {
    const raw = (priceInputs[index] ?? '0').replace(',', '.');
    const grossCents = eurosToCents(raw) ?? 0;
    updatePart(index, 'unitPriceGrossCents', grossCents);
    // Normalize the displayed text (e.g. "1.5" -> "1.50") from the same gross
    // cents just stored — never re-derived via net, so it can't drift (#1022).
    setPriceInputs((prev) =>
      prev.map((v, i) => (i === index ? (grossCents / 100).toFixed(2) : v))
    );
  };

  const handlePartRowKeyDown = (index: number, event: KeyboardEvent) => {
    if (!shouldAddProductRowOnEnter(event)) return;
    event.preventDefault();
    handleQuantityBlur(index);
    handlePriceBlur(index);
    focusNewPartNameRef.current = true;
    addPart();
  };

  // The hours field commits its draft on blur, which the focus move to the new
  // row triggers, so nothing typed into hours is lost (#3276).
  const handleLaborRowKeyDown = (event: KeyboardEvent) => {
    if (!shouldAddProductRowOnEnter(event)) return;
    event.preventDefault();
    focusNewLaborDescriptionRef.current = true;
    addLaborItem();
  };

  const handleVatChange = (index: number, newVatPercent: number) => {
    // The gross price the user sees/typed is unaffected by the VAT rate —
    // only the (server-derived) net split changes, so just update vatPercent.
    setParts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, vatPercent: newVatPercent } : item))
    );
    setIsDirty(true);
  };

  const removePart = (index: number) => {
    setParts((prev) => prev.filter((_, i) => i !== index));
    setPriceInputs((prev) => prev.filter((_, i) => i !== index));
    setQuantityInputs((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // Extras handlers
  const addExtra = () => {
    setExtras((prev) => [
      ...prev,
      { id: crypto.randomUUID(), description: '', amountGrossCents: 0, isTaxable: true, hasVariableQuantity: false, unit: null, quantity: null, sortOrder: prev.length },
    ]);
    setExtraPriceInputs((prev) => [...prev, '0.00']);
    setExtraQuantityInputs((prev) => [...prev, '']);
    setIsDirty(true);
  };

  const updateExtra = (index: number, field: keyof ExtraDraft, value: string | number | boolean | null) => {
    setExtras((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    setIsDirty(true);
  };

  const handleExtraPriceInputChange = (index: number, value: string) => {
    setExtraPriceInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handleExtraPriceBlur = (index: number) => {
    const raw = (extraPriceInputs[index] ?? '0').replace(',', '.');
    const cents = eurosToCents(raw) ?? 0;
    updateExtra(index, 'amountGrossCents', cents);
    setExtraPriceInputs((prev) =>
      prev.map((v, i) => (i === index ? (cents / 100).toFixed(2) : v))
    );
  };

  const handleExtraQuantityInputChange = (index: number, value: string) => {
    setExtraQuantityInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handleExtraQuantityBlur = (index: number) => {
    const raw = (extraQuantityInputs[index] ?? '0').replace(',', '.');
    const qty = parseFloat(raw);
    const validQty = isNaN(qty) || qty < 0 ? null : qty;
    updateExtra(index, 'quantity', validQty);
    setExtraQuantityInputs((prev) =>
      prev.map((v, i) => (i === index ? (validQty != null ? String(validQty) : '') : v))
    );
  };

  const removeExtra = (index: number) => {
    setExtras((prev) => prev.filter((_, i) => i !== index));
    setExtraPriceInputs((prev) => prev.filter((_, i) => i !== index));
    setExtraQuantityInputs((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // Hand-set status stays off PartDraft and the details PUT (#3568). A new row's
  // client UUID matches the persisted-id shape but is absent here until the server has it.
  const partOrderStatusById = useMemo(() => {
    const byId = new Map<string, { orderStatus: PartOrderStatus | null; orderStatusSetAt: string | null }>();
    for (const part of bookingServiceData?.parts ?? []) {
      byId.set(part.id, {
        orderStatus: part.orderStatus ?? null,
        orderStatusSetAt: part.orderStatusSetAt ?? null,
      });
    }
    return byId;
  }, [bookingServiceData?.parts]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Assigned mechanic */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('detail.assignedMechanic')}
          </Typography>
          {mechanicName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <UserAvatar
                firstName={mechanicName.split(' ')[0] ?? ''}
                lastName={mechanicName.split(' ').slice(1).join(' ') ?? ''}
                size={28}
              />
              <Typography variant="body2">{mechanicName}</Typography>
            </Box>
          )}
          <FormControl fullWidth size="small">
            <InputLabel id="service-detail-mechanic-label">{t('detail.changeMechanic')}</InputLabel>
            <Select
              labelId="service-detail-mechanic-label"
              id="service-detail-mechanic"
              value={selectedMechanicId}
              onChange={(e) => handleMechanicChange(e.target.value)}
              label={t('detail.changeMechanic')}
              disabled={saving}
            >
              <MenuItem value="">
                <em>{t('detail.noMechanic')}</em>
              </MenuItem>
              {mechanicMru.order(eligibleMechanics, (m) => m.id).map((m) => (
                <MenuItem
                  key={m.id}
                  value={m.id}
                  sx={hasSkillRequirements ? { color: 'success.main', fontWeight: 500 } : undefined}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box component="span" sx={{ flex: 1 }}>
                      {m.firstName} {m.lastName}
                    </Box>
                    <MechanicCertificateLock
                      blocked={mechanicCertificateIssue(m, requiredCertificateSkills) !== null}
                    />
                  </Box>
                </MenuItem>
              ))}
              {hasSkillRequirements && ineligibleMechanics.length > 0 && [
                <MenuItem key="__divider" disabled divider />,
                ...mechanicMru.order(ineligibleMechanics, (m) => m.id).map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box component="span" sx={{ flex: 1 }}>
                        {m.firstName} {m.lastName}
                      </Box>
                      <MechanicCertificateLock
                        blocked={mechanicCertificateIssue(m, requiredCertificateSkills) !== null}
                      />
                    </Box>
                  </MenuItem>
                )),
              ]}
            </Select>
          </FormControl>
          {selectedIsIneligible && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
              {t('detail.mechanicMissingSkills')}
            </Typography>
          )}
        </Box>

        <Divider />

        <TextField
          label={t('serviceTitle')}
          helperText={t('serviceTitleHint')}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setIsDirty(true);
          }}
          inputProps={{ maxLength: 200 }}
          size="small"
          fullWidth
        />

        {/* Task notes — customer-facing, shown on the invoice/work-order print (#1754) */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('detail.taskNotes')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder={t('detail.taskNotesPlaceholder')}
            helperText={t('detail.taskNotesHint')}
            value={notes ?? ''}
            onChange={(e) => {
              setNotes(e.target.value === '' ? null : e.target.value);
              setIsDirty(true);
            }}
          />
        </Box>

        <Divider />

        {/* Labor items */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detail.serviceLaborSteps')}
            </Typography>
            {laborEditable && (
              <Tooltip title={t('detail.addLaborItem')}>
                <IconButton size="small" onClick={addLaborItem} color="primary" aria-label={t('detail.addLaborItem')}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {laborLocked && (
            <Alert severity="info" role="status" sx={{ mb: 1 }}>
              {t('detail.fixedLaborLocked')}
            </Alert>
          )}
          {laborEditable && haynesProEnabled && haynesProBlocked && (
            <Alert severity="info" role="status" sx={{ mb: 1 }}>
              {t('detail.haynesProTime.saveFirst')}
            </Alert>
          )}
          {laborItems.map((item, index) => (
            <LaborItemRow
              key={item.clientKey}
              item={item}
              index={index}
              isEditable={!laborLocked}
              hoursEditable={laborEditable}
              workOrderId={workOrderId}
              serviceId={bookingServiceId}
              serviceStatus={serviceStatus}
              statusActionsLocked={statusActionsLocked}
              onUpdate={updateLaborItem}
              onRestoreHours={laborEditable ? restoreLaborItemHours : undefined}
              onRemove={laborEditable ? removeLaborItem : undefined}
              onRowKeyDown={laborEditable ? handleLaborRowKeyDown : undefined}
              descriptionInputRef={(el) => {
                laborDescriptionInputRefs.current[index] = el;
              }}
              laborRateCents={pricingType === 'fixed' ? null : laborRateCents}
              formatPrice={formatPrice}
              haynesProEnabled={haynesProEnabled}
              haynesProBlocked={haynesProBlocked}
              vehicleId={vehicleId}
              vehicleMake={vehicleMake}
              vehicleModel={vehicleModel}
              pricingType={pricingType}
              onHaynesProAdopted={applyHaynesProAdoption}
            />
          ))}
          {laborItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          )}
          {laborItems.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('detail.bookingDurationPreview', { duration: formatDuration(bookingDurationHours) })}
            </Typography>
          )}
          {hasScheduleConflict && allocatedSlotHours != null && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('detail.scheduleConflictWarning', {
                duration: formatDuration(bookingDurationHours),
                slot: formatDuration(allocatedSlotHours),
              })}
            </Alert>
          )}
        </Box>

        <Divider />

        {/* Parts */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detail.serviceParts')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PartBundlePicker onInsert={addBundleParts} />
              <Tooltip title={t('detail.addPart')}>
                <IconButton size="small" onClick={addPart} color="primary" aria-label={t('detail.addPart')}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {parts.map((item, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}
              onKeyDown={(event) => handlePartRowKeyDown(index, event)}
            >
              <PartCatalogInput
                value={item.name}
                onChange={(name) => updatePart(index, 'name', name)}
                onSelect={(part) => {
                  setParts((prev) => prev.map((row, i) => i === index ? {
                    ...row,
                    name: part.name,
                    // A slot from a required category keeps its category; the item fills it (#3842).
                    partCategoryId: row.partCategoryId ?? part.categoryId,
                    partId: part.id,
                    unitPriceGrossCents: part.sellPriceCents,
                  } : row));
                  setPriceInputs((prev) => prev.map((price, i) =>
                    i === index ? (part.sellPriceCents / 100).toFixed(2) : price));
                  setIsDirty(true);
                }}
                inputRef={(el) => { partNameInputRefs.current[index] = el; }}
              />
              <TextField
                size="small"
                sx={{ width: 80, flexShrink: 0 }}
                placeholder={t('detail.partQuantity')}
                value={quantityInputs[index] ?? ''}
                onChange={(e) => handleQuantityInputChange(index, e.target.value)}
                onBlur={() => handleQuantityBlur(index)}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <TextField
                size="small"
                sx={{ width: 110, flexShrink: 0 }}
                placeholder={t('detail.partPrice')}
                value={priceInputs[index] ?? '0.00'}
                onChange={(e) => handlePriceInputChange(index, e.target.value)}
                onBlur={() => handlePriceBlur(index)}
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  input: {
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  },
                }}
              />
              <VatRatePicker
                value={item.vatPercent}
                rates={vatRates}
                onChange={(percent) => handleVatChange(index, percent)}
                label={t('detail.partVat')}
              />
              <TextField
                size="small"
                type="number"
                sx={{ width: 90, flexShrink: 0 }}
                placeholder={t('detail.partDiscount')}
                value={item.discountPercent}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  updatePart(index, 'discountPercent', Math.max(0, Math.min(100, val)));
                }}
                slotProps={{
                  htmlInput: { min: 0, max: 100, step: '0.1' },
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  },
                }}
              />
              <PartOrderStatusSelect
                workOrderId={workOrderId}
                bookingServiceId={bookingServiceId}
                partId={item.id !== undefined && partOrderStatusById.has(item.id) ? item.id : undefined}
                value={(item.id ? partOrderStatusById.get(item.id)?.orderStatus : null) ?? null}
                orderStatusSetAt={item.id ? partOrderStatusById.get(item.id)?.orderStatusSetAt ?? null : null}
              />
              <Typography variant="body2" sx={{ flexShrink: 0, minWidth: 80, textAlign: 'right', fontWeight: 500 }}>
                {/* Discount-aware (#1815) — used to ignore item.discountPercent, so the
                    figure shown here didn't match what the customer is actually billed. */}
                = {(lineNetAfterDiscountCents(item.unitPriceGrossCents, item.quantity, item.discountPercent) / 100).toFixed(2)} €
              </Typography>
              <Tooltip title={t('detail.removePart')}>
                <IconButton
                  size="small"
                  onClick={() => removePart(index)}
                  color="error"
                  tabIndex={-1}
                  aria-label={t('detail.removePart')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          {parts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Extras */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detail.serviceExtras')}
            </Typography>
            <Tooltip title={t('detail.addExtra')}>
              <IconButton size="small" onClick={addExtra} color="primary" aria-label={t('detail.addExtra')}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {extras.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
                placeholder={t('detail.extraDescription')}
                value={item.description}
                onChange={(e) => updateExtra(index, 'description', e.target.value)}
              />
              {item.hasVariableQuantity ? (
                <>
                  <TextField
                    size="small"
                    sx={{ width: 110, flexShrink: 0 }}
                    placeholder={t('detail.extraUnitPrice')}
                    value={extraPriceInputs[index] ?? '0.00'}
                    onChange={(e) => handleExtraPriceInputChange(index, e.target.value)}
                    onBlur={() => handleExtraPriceBlur(index)}
                    slotProps={{
                      htmlInput: { inputMode: 'decimal' },
                      input: {
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        endAdornment: item.unit ? (
                          <InputAdornment position="end">/{item.unit}</InputAdornment>
                        ) : undefined,
                      },
                    }}
                  />
                  <TextField
                    size="small"
                    sx={{ width: 100, flexShrink: 0 }}
                    placeholder={t('detail.extraQuantity')}
                    value={extraQuantityInputs[index] ?? ''}
                    onChange={(e) => handleExtraQuantityInputChange(index, e.target.value)}
                    onBlur={() => handleExtraQuantityBlur(index)}
                    slotProps={{
                      htmlInput: { inputMode: 'decimal' },
                    }}
                  />
                  <UnitSelect
                    value={item.unit}
                    onChange={(unit) => updateExtra(index, 'unit', unit)}
                    label={t('detail.extraUnit')}
                  />
                  <Typography variant="body2" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                    = {(lineTotalGrossCents(item.amountGrossCents, item.quantity ?? 0) / 100).toFixed(2)} €
                  </Typography>
                </>
              ) : (
                <TextField
                  size="small"
                  sx={{ width: 120, flexShrink: 0 }}
                  placeholder={t('detail.extraAmount')}
                  value={extraPriceInputs[index] ?? '0.00'}
                  onChange={(e) => handleExtraPriceInputChange(index, e.target.value)}
                  onBlur={() => handleExtraPriceBlur(index)}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal' },
                    input: {
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    },
                  }}
                />
              )}
              <Tooltip title={t('detail.removeExtra')}>
                <IconButton
                  size="small"
                  onClick={() => removeExtra(index)}
                  color="error"
                  tabIndex={-1}
                  aria-label={t('detail.removeExtra')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          {extras.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </Box>

        {/* Action row: remove service + autosave status (#557) */}
        <Box sx={{ display: 'flex', gap: 1, pt: 1, alignItems: 'center' }}>
          <Button
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={removing}
          >
            {t('detail.removeService')}
          </Button>
          <Box sx={{ flex: 1 }} />
          {updateDetails.isPending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
              <CircularProgress size={14} />
              <Typography variant="caption">{t('detail.autosaveSaving')}</Typography>
            </Box>
          ) : updateDetails.isError || localSaveError ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ErrorOutlineIcon fontSize="small" color="error" />
              <Typography variant="caption" color="error">
                {t(conflictBlocked ? 'detail.autosaveConflict' : 'detail.detailsSaveFailed')}
              </Typography>
              <Button size="small" onClick={() => {
                if (!conflictBlocked) { void autosave(); return; }
                const incoming = incomingBaseRef.current;
                const source = incoming && (!baseRef.current || Date.parse(incoming.updatedAt) >= Date.parse(baseRef.current.updatedAt)) ? incoming : baseRef.current;
                if (source) {
                  baseRef.current = source; const data = serviceDetailsPayload(source.service);
                  setNotes(data.notes ?? null); setLaborItems(prev => mergeLaborItemDrafts(prev, source.service.laborItems));
                  setParts(toPartDrafts(data.parts)); setExtras(data.extras ?? []);
                  setPriceInputs((data.parts ?? []).map(row => (row.unitPriceGrossCents / 100).toFixed(2)));
                  setQuantityInputs((data.parts ?? []).map(row => String(row.quantity)));
                  setExtraPriceInputs((data.extras ?? []).map(row => (row.amountGrossCents / 100).toFixed(2)));
                  setExtraQuantityInputs((data.extras ?? []).map(row => row.quantity == null ? '' : String(row.quantity)));
                }
                conflictBlockedRef.current = false; setConflictBlocked(false); setLocalSaveError(false);
                updateDetails.reset(); setIsDirty(false); setReloadGeneration(n => n + 1); onSaved?.();
              }}>
                {t(conflictBlocked ? 'detail.autosaveDiscardReload' : 'detail.autosaveRetry')}
              </Button>
            </Box>
          ) : isDirty ? (
            <Typography variant="caption" color="text.secondary">
              {t('detail.autosaveUnsaved')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
              <CheckCircleIcon fontSize="small" />
              <Typography variant="caption">{t('detail.autosaveSaved')}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title={t('detail.removeService')}
        message={t('detail.removeServiceConfirm')}
        confirmColor="error"
        onConfirm={() => {
          setConfirmOpen(false);
          onRemove(bookingServiceId);
        }}
        onCancel={() => setConfirmOpen(false)}
        loading={removing}
      />
      <CertificateOverrideDialog
        open={certificateGate !== null}
        submitting={overrideSubmitting}
        blocked={certificateGate?.blocked ?? []}
        onClose={handleCertificateOverrideClose}
        onSubmit={handleCertificateOverrideSubmit}
      />
    </>
  );
}
